import { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Receipt, Save, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const INR = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

export default function Billing() {
  const { user } = useAuth();
  const canEdit = ['admin', 'billing'].includes(user.role);
  const [societies, setSocieties] = useState([]);
  const [societyId, setSocietyId] = useState('');
  const [profile, setProfile] = useState({ rent_amount: 0, payable_terms: '', billing_status: 'active', contact: '' });
  const [ledger, setLedger] = useState({ entries: [], balance: 0 });
  const [entry, setEntry] = useState({ date: '', description: '', debit: 0, credit: 0 });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get('/societies').then((r) => { setSocieties(r.data); if (r.data[0]) setSocietyId(user.society_id || r.data[0].id); }); }, [user.society_id]);

  const load = useCallback(() => {
    if (!societyId) return;
    api.get(`/societies/${societyId}/billing`).then((r) => r.data && setProfile(r.data));
    api.get(`/societies/${societyId}/ledger`).then((r) => setLedger(r.data));
  }, [societyId]);
  useEffect(() => { load(); }, [load]);

  const saveProfile = async () => {
    setSaving(true);
    try { await api.post(`/societies/${societyId}/billing`, { ...profile, rent_amount: Number(profile.rent_amount) }); toast.success('Billing profile saved'); }
    catch { toast.error('Not permitted'); }
    setSaving(false);
  };
  const addEntry = async () => {
    if (!entry.date || !entry.description) return toast.error('Date & description required');
    try { await api.post(`/societies/${societyId}/ledger`, { ...entry, debit: Number(entry.debit), credit: Number(entry.credit) }); setEntry({ date: '', description: '', debit: 0, credit: 0 }); load(); toast.success('Ledger entry added'); }
    catch { toast.error('Not permitted'); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div><p className="overline text-primary">Billing Setup</p><h1 className="font-head text-3xl font-bold">Billing & Ledger</h1></div>
        <Select value={societyId} onValueChange={setSocietyId} disabled={user.role === 'society_manager'}>
          <SelectTrigger data-testid="billing-society-select" className="w-[210px]"><SelectValue /></SelectTrigger>
          <SelectContent>{societies.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="font-head text-lg font-bold flex items-center gap-2"><Receipt className="h-4 w-4 text-primary" /> Billing Profile</p>
          <div className="mt-3 space-y-2">
            <div><Label>Rent / Payable (monthly)</Label><Input type="number" data-testid="billing-rent" disabled={!canEdit} value={profile.rent_amount} onChange={(e) => setProfile({ ...profile, rent_amount: e.target.value })} /></div>
            <div><Label>Payable Terms</Label><Input data-testid="billing-terms" disabled={!canEdit} value={profile.payable_terms} onChange={(e) => setProfile({ ...profile, payable_terms: e.target.value })} /></div>
            <div><Label>Status</Label>
              <Select value={profile.billing_status} onValueChange={(v) => setProfile({ ...profile, billing_status: v })} disabled={!canEdit}>
                <SelectTrigger data-testid="billing-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="overdue">Overdue</SelectItem><SelectItem value="paused">Paused</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Billing Contact</Label><Input data-testid="billing-contact" disabled={!canEdit} value={profile.contact} onChange={(e) => setProfile({ ...profile, contact: e.target.value })} /></div>
            {canEdit && <Button className="w-full" onClick={saveProfile} disabled={saving} data-testid="save-billing-btn">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save Profile</Button>}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4 lg:col-span-2">
          <div className="flex items-center justify-between">
            <p className="font-head text-lg font-bold">Ledger</p>
            <span className="rounded-full border border-border px-3 py-1 font-mono text-sm">Balance: <b className={ledger.balance >= 0 ? 'text-emerald-400' : 'text-rose-400'}>{INR(ledger.balance)}</b></span>
          </div>
          <div className="mt-3 overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead><TableHead className="text-right">Balance</TableHead></TableRow></TableHeader>
              <TableBody>
                {ledger.entries.map((e) => (
                  <TableRow key={e.id} data-testid={`ledger-row-${e.id}`}>
                    <TableCell className="font-mono text-xs">{e.date}</TableCell>
                    <TableCell>{e.description}</TableCell>
                    <TableCell className="text-right font-mono text-rose-400">{e.debit ? INR(e.debit) : '-'}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-400">{e.credit ? INR(e.credit) : '-'}</TableCell>
                    <TableCell className="text-right font-mono">{INR(e.balance)}</TableCell>
                  </TableRow>
                ))}
                {!ledger.entries.length && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">No entries.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
          {canEdit && (
            <div className="mt-3 grid grid-cols-2 gap-2 border-t border-border pt-3 sm:grid-cols-5">
              <Input type="date" data-testid="ledger-date" value={entry.date} onChange={(e) => setEntry({ ...entry, date: e.target.value })} />
              <Input placeholder="Description" data-testid="ledger-desc" className="sm:col-span-2" value={entry.description} onChange={(e) => setEntry({ ...entry, description: e.target.value })} />
              <Input type="number" placeholder="Debit" data-testid="ledger-debit" value={entry.debit} onChange={(e) => setEntry({ ...entry, debit: e.target.value })} />
              <Input type="number" placeholder="Credit" data-testid="ledger-credit" value={entry.credit} onChange={(e) => setEntry({ ...entry, credit: e.target.value })} />
              <Button className="col-span-2 sm:col-span-5" onClick={addEntry} data-testid="add-ledger-btn"><Plus className="mr-1 h-4 w-4" /> Add Entry</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
